import React, { useEffect, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CurrentUserContext } from "../../context/CurrentUserContext";
import "./MyHistory.css";
import { useTranslation } from "../../utils/hooks/translateHook";
import { getCourseProgress } from "../../utils/API/progressApi.js";
import { getCourseAccessLevel } from "../../utils/getCourseAcessLevel.js";
import { getCoursePackageData } from "../../utils/getCoursePackageData.js";
import { transformLevelContent } from "../../utils/transformLevelContent.js";


function MyHistory() {
  const t = useTranslation();
  const navigate = useNavigate();
  const { currentUser } = useContext(CurrentUserContext);
  const [coursesHistory, setCoursesHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const loadAllCoursesHistory = async () => {
      try {
        setLoading(true);
        const userCourses = currentUser?.courses || [];
        const results = await Promise.all(
          userCourses.map(async (course) => {
            try {
              const data = await getCourseProgress(course.courseSlug);
              return {
                courseSlug: course.courseSlug,
                level: course.level,
                lastLesson: data?.lastLesson || null,
                courseProgress: data?.courseProgress || null,
                historyItems:
                  data?.courseProgress?.progress || [],
              };
            } catch (error) {
              console.error(`Error loading history for ${course.courseSlug}:`, error);

              return {
                courseSlug: course.courseSlug,
                level: course.level,
                lastLesson: null,
                courseProgress: null,
                historyItems: [],
              };
            }
          })
        );

        console.log("ALL COURSES HISTORY:", results);
        setCoursesHistory(results);
      } catch (error) {
        console.error("Error loading all courses history:", error);
        setCoursesHistory([]);
      } finally {
        setLoading(false);
      }
    };

    if (currentUser?.courses?.length > 0) {
      loadAllCoursesHistory();
    } else {
      setCoursesHistory([]);
      setLoading(false);
    }
  }, [currentUser]);


const getLastActivity = (historyItems) => {
  const dates = historyItems
    .map((item) => item.completedAt || item.lastVisitedAt)
    .filter(Boolean)
    .map((date) => new Date(date));

  if (dates.length === 0) {
    return null;
  }
  return new Date( Math.max(...dates.map((date) => date.getTime())));
};

  const openLesson = (courseSlug, item) => {
    if (!item) return;
    const lessonPath = item.lessonType === "video" ? "videos" : "lectures";
    navigate(`/course/${courseSlug}/${lessonPath}/${item.lessonSlug}`);
  };

  const continueCourse = (course, inProgressLessons, completedLessons) => {
    const lesson = course.lastLesson?.lastLesson || course.lastLesson?.lesson || course.lastLesson;
    if (lesson?.lessonSlug && lesson?.lessonType) {
      openLesson(course.courseSlug, lesson);
      return;
    }
    if (inProgressLessons.length > 0) {
      openLesson(course.courseSlug, inProgressLessons[0]);
      return;
    }
    if (completedLessons.length > 0) {
      openLesson(course.courseSlug, completedLessons[0]);
    }
  };

  const formatCourseName = (slug) => {
    if (!slug) return "";
    return slug.replace(/-/g, " ").replace(/\b\w/g, (letter) =>
        letter.toUpperCase()
      );
  };

  const getTotalAccessibleLessons = (courseSlug) => {
    const courseAccessLevel = getCourseAccessLevel(currentUser, courseSlug);
    const rawCourseData = getCoursePackageData(courseSlug);
    if (!rawCourseData?.length) return 0;
    const transformedData = transformLevelContent(rawCourseData);
    const accessibleLevels = courseAccessLevel === 9999 ? transformedData.filter((level) => level.level !== 9999)
        : transformedData.filter( (level) => level.level <= courseAccessLevel && level.level !== 9999
          );

    return accessibleLevels.reduce((total, level) => {
      const lessonsInLevel = (level.subjects || []).reduce(
        (subjectTotal, subject) => {
          const videos = subject.videos?.length || 0;
          const lectures = subject.lectures?.length || 0;
          return subjectTotal + videos + lectures;
        },0
      );
      return total + lessonsInLevel;
    }, 0);
  };

  const renderLesson = (courseSlug, item) => {
    const progress = item.completedAt ? 100 : Math.round(Number(item.watchedPercent || 0));
    return (
      <div className="history-lesson-row"
        key={item._id || item.lessonSlug}
      >
        <div className={`history-lesson-status ${ item.completedAt ? "completed" : "progress"}`}>
          {item.completedAt ? "✓" : "▶"}
        </div>
        <button className="history-lesson-title"
          type="button"
          onClick={() => openLesson(courseSlug, item)}
        >
          {item.title || item.lessonTitle || item.lessonSlug}
        </button>
        {!item.completedAt ? (
          <div className="lesson-progress-wrapper">
            <div className="lesson-progress">
              <div className="lesson-progress-fill"
                style={{width: `${Math.min(Math.max(progress, 0), 100)}%`,}}
              />
            </div>
            <span className="history-lesson-percent"
              style={{left: `${Math.min(Math.max(progress, 8), 92)}%`,}}>
              {progress}%
            </span>
          </div>
        ) : (
          <div className="history-completed-bar">
            {t("completed")} {progress}%
          </div>
        )}
        {item.lastVisitedAt && (
          <span className="lesson-last-visited">
            {t("lastVisited")}:{" "}
            {new Date(item.lastVisitedAt).toLocaleDateString()}
          </span>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <section className="my-history">
        <p className="history-loading"> {t("loadingCourseHistory")} </p>
      </section>
    );
  }

  return (
   <section className="my-history">
    <div className="history-page-header">
      <h1>{t("myCourseHistory").toUpperCase()}</h1>
    </div>
    {coursesHistory.length === 0 ? (
      <div className="history-empty">
         <p>{t("noCourseHistory")}</p>
      </div>
    ) : (
      <div className="history-courses-list">
        {coursesHistory.map((course) => {
          const completedLessons = course.historyItems
            .filter((item) => item.completedAt)
            .sort((a, b) =>
                new Date(b.completedAt) -
                new Date(a.completedAt)
            );

          const inProgressLessons = course.historyItems
            .filter((item) => !item.completedAt && Number(item.watchedPercent || 0) > 0)
            .sort((a, b) =>
                new Date(b.lastVisitedAt || 0) -
                new Date(a.lastVisitedAt || 0)
            );

          const lastActivity = getLastActivity(course.historyItems);
          const completedCount = completedLessons.length;
          const totalLessons = getTotalAccessibleLessons(course.courseSlug);
          const courseProgressPercent = totalLessons > 0 ? Math.round((completedCount / totalLessons) * 100) : 0;

          return (
            <div className={`history-course-card history-course-${course.courseSlug}`}
                key={course.courseSlug}
            >
              <div className="history-course-header">
                <div className="history-course-name-wrapper">
                  <h2>{formatCourseName(course.courseSlug)}</h2>
                </div>
              <div className="history-course-percentage">
                <span className="history-course-count">
                  {completedCount} / {totalLessons} {t("lessonsCompleted")}
                </span>
                <strong>{courseProgressPercent}%</strong>
              </div>
              </div>
              <div className="history-course-summary">
                <div className="history-summary-bottom">
                  <span>{lastActivity ? `Last activity: ${lastActivity.toLocaleDateString()}` : "No activity yet"}</span>
                </div>
              </div>

              {inProgressLessons.length > 0 && (
                <div className="history-lessons-section">
                  <div className="history-section-header">
                    <h3> {t("inProgress")} </h3>
                    <span> {inProgressLessons.length}</span>
                  </div>
                  <div className="history-lessons-list">
                    {inProgressLessons.map((item) =>
                      renderLesson(course.courseSlug, item)
                    )}
                  </div>
                </div>
              )}
              {completedLessons.length > 0 && (
                <div className="history-lessons-section">
                  <div className="history-section-header">
                    <h3> {t("completed")} </h3>
                    <span>{completedLessons.length}</span>
                  </div>
                  <div className="history-lessons-list">
                    {completedLessons.map((item) => renderLesson(course.courseSlug, item))}
                  </div>
                </div>
              )}
              {completedLessons.length === 0 &&
                inProgressLessons.length === 0 && (
                  <div className="history-lessons-section">
                    <p className="history-no-activity">
                       {t("noLearningActivity")}
                    </p>
                  </div>
              )}
            </div>
          );
        })}
      </div>
    )}
   </section>
  );
}

export default MyHistory;